import { FlowAssetDoc } from '@upflowy/types';
import { Dispatch, SetStateAction } from 'react';

export declare type Asset = {
  id?: string;
  name?: string;
  type: string;
  url: string;
};

class AssetRegistry {
  static priorityAssetCount = 0;

  private container: HTMLElement;

  private registry: Record<string, Asset> = {};

  constructor(el: HTMLElement) {
    this.container = el;
  }

  find(asset: Asset) {
    return this.registry[asset.url];
  }

  private setIdAndName(element: HTMLScriptElement | HTMLLinkElement, asset: FlowAssetDoc) {
    if (asset.id) element.setAttribute('u-id', asset.id);
    if (asset.name) element.setAttribute('u-name', asset.name);
    this.registry[asset.url] = asset;
  }

  private createScript(asset: FlowAssetDoc) {
    const scriptElement = document.createElement('script');
    this.setIdAndName(scriptElement, asset);
    scriptElement.setAttribute('src', asset.url);
    scriptElement.setAttribute('type', 'text/javascript');
    return scriptElement;
  }

  insert(
    asset: FlowAssetDoc,
    priorityAssetsExpected: number,
    setDependenciesLoaded: Dispatch<SetStateAction<boolean>> | undefined,
  ) {
    const priorityLoad =
      (asset.priority && asset.priority === 1) || asset.name === 'gjs-base-components';

    switch (asset.type) {
      case 'js': {
        const script = this.createScript(asset);
        if (priorityLoad) {
          this.container.prepend(script);

          script.onload = () => {
            AssetRegistry.priorityAssetCount += 1;
            if (priorityAssetsExpected === AssetRegistry.priorityAssetCount)
              setDependenciesLoaded && setDependenciesLoaded(true);
          };

          return this;
        }

        this.container.appendChild(script);
        return this;
      }
      case 'css': {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = asset.url;
        this.setIdAndName(link, asset);

        this.container.appendChild(link);
        return this;
      }
      default:
        throw Error(`Unknown asset type ${asset.type}, id ${asset.id}, url ${asset.url}`);
    }
  }

  insertNewAsset(
    asset: FlowAssetDoc,
    expectedPriorityAssets: number,
    setDependenciesLoaded: Dispatch<SetStateAction<boolean>> | undefined,
  ) {
    const existingAsset = this.find(asset);
    if (!existingAsset) {
      this.insert(asset, expectedPriorityAssets, setDependenciesLoaded);
    }
    return this;
  }
}

export default AssetRegistry;
